import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, FileText, CheckCircle, ArrowRight, Phone, Mail, User, Github } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function ApplicationForm() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [formData, setFormData] = useState({});
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const fetchRole = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/roles`);
      const foundRole = res.data.find((r) => r.id === roleId);
      if (!foundRole) {
        toast.error("Role not found");
        navigate("/");
        return;
      }
      setRole(foundRole);
      if (foundRole.status !== "open") {
        toast.error("Applications for this role are closed");
        navigate("/");
        return;
      }
      // Initialize form data with empty strings for each question
      const initialData = {};
      (foundRole.questions || []).forEach((q) => {
        initialData[q.id] = "";
      });
      setFormData(initialData);
    } catch (err) {
      toast.error("Failed to load role details");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [roleId, navigate]);

  useEffect(() => {
    if (!roleId) {
      navigate("/");
      return;
    }
    fetchRole();
  }, [roleId, navigate, fetchRole]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const isValidGithubUrl = (value) => {
    try {
      const parsed = new URL(value);
      const host = parsed.hostname.toLowerCase();
      const isGithubHost = host === "github.com" || host === "www.github.com";
      return isGithubHost && parsed.pathname.replace(/\//g, "").length > 0;
    } catch {
      return false;
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && f.type !== "application/pdf") {
      toast.error("Only PDF files are accepted");
      return;
    }
    if (f && f.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.type !== "application/pdf") {
      toast.error("Only PDF files are accepted");
      return;
    }
    if (f && f.size > 10 * 1024 * 1024) {
      toast.error("File size must be under 10MB");
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check required questions
    const questions = role?.questions || [];
    for (const q of questions) {
      if (q.required && !formData[q.id]?.trim()) {
        toast.error(`Please fill in: ${q.label}`);
        return;
      }
    }

    // Check for github_url in questions
    const githubQuestion = questions.find((q) => q.type === "url" && q.id === "github_url");
    if (githubQuestion && formData[githubQuestion.id] && !isValidGithubUrl(formData[githubQuestion.id])) {
      toast.error("Please enter a valid GitHub URL");
      return;
    }

    // Check other url fields
    const urlQuestions = questions.filter((q) => q.type === "url" && q.id !== "github_url");
    for (const q of urlQuestions) {
      if (formData[q.id]?.trim() && !isValidUrl(formData[q.id])) {
        toast.error(`Please enter a valid URL for: ${q.label}`);
        return;
      }
    }

    if (!file) {
      toast.error("Resume is required");
      return;
    }

    setSubmitting(true);
    const submitFormData = new FormData();
    submitFormData.append("name", formData.name || "");
    submitFormData.append("email", formData.email || "");
    submitFormData.append("mobile", formData.mobile || "");
    submitFormData.append("github_url", formData.github_url || "");
    submitFormData.append("role", roleId);
    submitFormData.append("form_data", JSON.stringify(formData));
    submitFormData.append("resume", file);

    try {
      await axios.post(`${API}/applications`, submitFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Application submitted successfully. You will be notified soon.");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to submit application";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const isValidUrl = (value) => {
    try {
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const renderField = (question, index) => {
    const value = formData[question.id] || "";

    if (question.type === "url") {
      const Icon = question.id === "github_url" ? Github : FileText;
      return (
        <motion.div key={question.id} variants={fadeUp} initial="hidden" animate="visible" custom={index + 2} style={{ marginTop: "3rem" }}>
          <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-4 block">
            {question.label}
          </Label>
          <div className="relative">
            <Icon className="absolute left-0 top-4 w-4 h-4 text-zinc-700" />
            <Input
              name={question.id}
              type="url"
              value={value}
              onChange={handleChange}
              placeholder={question.placeholder}
              className="h-14 bg-transparent border-0 border-b border-zinc-800 rounded-none focus:border-white focus:ring-0 pl-7 text-lg text-white placeholder:text-zinc-700 transition-colors duration-300"
            />
          </div>
        </motion.div>
      );
    }

    return (
      <motion.div key={question.id} variants={fadeUp} initial="hidden" animate="visible" custom={index + 2} style={{ marginTop: "3rem" }}>
        <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-4 block">
          {question.label}
        </Label>
        <Textarea
          name={question.id}
          value={value}
          onChange={handleChange}
          placeholder={question.placeholder}
          rows={5}
          className="bg-transparent border border-zinc-800 rounded-none focus:border-white focus:ring-0 text-white placeholder:text-zinc-700 resize-none transition-colors duration-300"
        />
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-zinc-800 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="text-center px-8"
          data-testid="submission-success"
        >
          <div className="w-16 h-16 border border-[#BEF264]/30 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-8 h-8 text-[#BEF264]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
            Application Received
          </h1>
          <p className="text-zinc-500 text-base sm:text-lg max-w-md mx-auto mb-10 font-light">
            We&apos;ll review your application and notify you soon. Thank you for your interest in BlankSage.
          </p>
          <Button
            data-testid="submit-another-btn"
            onClick={() => navigate("/")}
            className="h-12 px-8 bg-transparent border border-zinc-800 text-zinc-400 hover:bg-white hover:text-black font-mono text-xs uppercase tracking-widest transition-colors duration-300"
          >
            Back to Careers
          </Button>
        </motion.div>
      </div>
    );
  }

  const questions = role?.questions || [];

  return (
    <div className="min-h-screen bg-[#050505] relative">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <img
            src="https://blanksage-public-assets.s3.ap-south-1.amazonaws.com/bstext.png"
            alt="Blanksage"
            className="h-6 sm:h-7 brightness-0 invert"
            data-testid="header-logo"
          />
          <button
            onClick={() => navigate("/")}
            className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.15em] hover:text-zinc-400 transition-colors duration-300"
          >
            Back to Careers
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-32 pb-20">
        <div className="grid lg:grid-cols-5 gap-16 lg:gap-24">
          {/* Left - Manifesto */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={0}
            className="lg:col-span-2 lg:sticky lg:top-32 lg:self-start"
          >
            <div className="mb-6">
              <div className="w-10 h-[2px] bg-[#BEF264] mb-6" />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-4">
                {role?.title || "Apply"}
              </h1>
              <p className="text-zinc-500 text-sm sm:text-base leading-relaxed max-w-sm font-light">
                We&apos;re looking for people who build things that matter. Tell us about yourself and what drives you.
              </p>
            </div>

            {role && (
              <div className="border border-zinc-900 bg-black/40 p-5 mb-6 space-y-4">
                <h2 className="text-white text-lg sm:text-xl font-semibold tracking-tight">
                  {role.description}
                </h2>

                <div
                  dangerouslySetInnerHTML={{ __html: role.details }}
                  className="text-zinc-400 text-xs sm:text-sm leading-relaxed [&_strong]:text-zinc-300 [&_strong]:font-medium [&_ul]:list-disc [&_ul]:pl-4 [&_li]:mb-1"
                />

                <p className="text-zinc-400 text-xs sm:text-sm leading-relaxed">
                  If you learn quickly and build with intent, we&apos;d like to hear from you.
                </p>
              </div>
            )}

            <div className="border-t border-zinc-900 pt-5 grid grid-cols-3 gap-2">
              <div className="border border-zinc-900 p-2 text-center">
                <p className="text-zinc-300 text-[11px] font-medium">Details</p>
                <p className="text-zinc-600 text-[10px] mt-1">Fill form</p>
              </div>
              <div className="border border-zinc-900 p-2 text-center">
                <p className="text-zinc-300 text-[11px] font-medium">Resume</p>
                <p className="text-zinc-600 text-[10px] mt-1">PDF, max 10MB</p>
              </div>
              <div className="border border-zinc-900 p-2 text-center">
                <p className="text-zinc-300 text-[11px] font-medium">Interest</p>
                <p className="text-zinc-600 text-[10px] mt-1">Share passion</p>
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} data-testid="application-form" className="space-y-10">
              {/* Name and Email row */}
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-4 block">
                      Full Name
                    </Label>
                    <div className="relative">
                      <User className="absolute left-0 top-4 w-4 h-4 text-zinc-700" />
                      <Input
                        data-testid="input-name"
                        name="name"
                        value={formData.name || ""}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        className="h-14 bg-transparent border-0 border-b border-zinc-800 rounded-none focus:border-white focus:ring-0 pl-7 text-lg text-white placeholder:text-zinc-700 transition-colors duration-300"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-4 block">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-0 top-4 w-4 h-4 text-zinc-700" />
                      <Input
                        data-testid="input-email"
                        name="email"
                        type="email"
                        value={formData.email || ""}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        className="h-14 bg-transparent border-0 border-b border-zinc-800 rounded-none focus:border-white focus:ring-0 pl-7 text-lg text-white placeholder:text-zinc-700 transition-colors duration-300"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Mobile */}
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
                <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-4 block">
                  Mobile Number
                </Label>
                <div className="relative">
                  <Phone className="absolute left-0 top-4 w-4 h-4 text-zinc-700" />
                  <Input
                    data-testid="input-mobile"
                    name="mobile"
                    type="tel"
                    value={formData.mobile || ""}
                    onChange={handleChange}
                    placeholder="+1 (555) 000-0000"
                    className="h-14 bg-transparent border-0 border-b border-zinc-800 rounded-none focus:border-white focus:ring-0 pl-7 text-lg text-white placeholder:text-zinc-700 transition-colors duration-300"
                  />
                </div>
              </motion.div>

              {/* Dynamic Questions */}
              {questions.map((question, index) => renderField(question, index))}

              {/* Resume Upload */}
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={questions.length + 3}>
                <Label className="text-zinc-600 font-mono text-[10px] uppercase tracking-[0.2em] mb-4 block">
                  Resume
                </Label>
                <div
                  data-testid="resume-upload-area"
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  className={`border border-dashed cursor-pointer group transition-colors duration-300 p-8 ${
                    file
                      ? "border-[#BEF264]/30 bg-[#BEF264]/[0.02]"
                      : "border-zinc-800 hover:border-zinc-600"
                  }`}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="resume-file-input"
                  />
                  {file ? (
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 border border-[#BEF264]/20 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#BEF264]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{file.name}</p>
                        <p className="text-zinc-600 text-xs mt-0.5">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="text-zinc-600 hover:text-white text-xs font-mono uppercase tracking-widest transition-colors duration-300"
                        data-testid="remove-file-btn"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-zinc-700 mx-auto mb-3 group-hover:text-zinc-500 transition-colors duration-300" />
                      <p className="text-zinc-500 text-sm">
                        Drop your resume here or{" "}
                        <span className="text-white underline underline-offset-4">browse</span>
                      </p>
                      <p className="text-zinc-700 text-xs mt-1">PDF only, max 10MB</p>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Submit */}
              <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={questions.length + 4}>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-application-btn"
                  className="h-14 px-12 bg-white text-black hover:bg-[#BEF264] hover:text-black font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 w-full sm:w-auto group disabled:opacity-50"
                >
                  {submitting ? (
                    <span className="flex items-center gap-3">
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Submitting
                    </span>
                  ) : (
                    <span className="flex items-center gap-3">
                      Submit Application
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  )}
                </Button>
              </motion.div>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-8 px-6 sm:px-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img
            src="https://blanksage-public-assets.s3.ap-south-1.amazonaws.com/bs+only+logo.png"
            alt="Blanksage"
            className="h-5 opacity-30 brightness-0 invert"
          />
          <p className="text-zinc-700 font-mono text-[10px] uppercase tracking-[0.15em]">
            Blanksage Careers
          </p>
        </div>
      </footer>
    </div>
  );
}